import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useEditorTheme } from "../theme.js";
import McqAnswer from "../components/McqAnswer.js";
import {
  api,
  type AiAward,
  type AiReviewResponse,
  type SubmissionDetail,
  type SubmissionSummary,
} from "../api.js";

/** Review one submission: the student's code, per-test detail, and a marks override. */
export default function GradeSubmission() {
  const editorTheme = useEditorTheme();
  const { id } = useParams<{ id: string }>();
  const [s, setS] = useState<SubmissionDetail | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [marks, setMarks] = useState<string>("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  /**
   * The next submission still waiting on a teacher, so marking a set is one
   * pass instead of a round trip through the list for every student.
   */
  const [nextId, setNextId] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setStatus(null);
    api
      .get<{ submission: SubmissionDetail; isStaff: boolean }>(`/api/submissions/${id}`)
      .then((r) => {
        setS(r.submission);
        setIsStaff(r.isStaff);
        // Pre-fill with the auto score so a teacher who agrees just clicks Save.
        setMarks(String(r.submission.manualScore ?? r.submission.autoScore ?? ""));
        setComment(r.submission.teacherComment ?? "");
        return r.isStaff ? r.submission.assignment.id : null;
      })
      .then((assignmentId) => {
        if (!assignmentId) return;
        return api
          .get<{ submissions: SubmissionSummary[] }>(
            `/api/assignments/${assignmentId}/submissions`
          )
          .then((r) => {
            // Latest attempt per student, oldest first: the order a teacher
            // would work through them.
            const seen = new Set<string>();
            const latest = r.submissions.filter((x) => {
              const key = x.student?.id ?? "";
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            const queue = latest.filter((x) => x.manualScore === null && x.id !== id).reverse();
            setRemaining(queue.length);
            setNextId(queue[0]?.id ?? null);
          });
      })
      .catch((e) => setError(e.message));
  }, [id]);

  async function saveGrade(thenNext = false) {
    if (!id) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await api.post(`/api/submissions/${id}/grade`, {
        manualScore: marks === "" ? null : Number(marks),
        teacherComment: comment,
      });
      if (thenNext && nextId) {
        navigate(`/submissions/${nextId}`);
        return;
      }
      setStatus("Grade saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the grade.");
    } finally {
      setSaving(false);
    }
  }

  if (error && !s) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!s) return <div className="page muted">Loading…</div>;

  const passed = s.testResults.filter((r) => r.passed).length;
  const isMcq = s.assignment.kind === "MCQ";

  return (
    <div className="page">
      <Link to={`/assignments/${s.assignment.id}/submissions`} className="muted" style={{ fontSize: 13 }}>
        ← Back to submissions
      </Link>

      <div className="between" style={{ margin: "10px 0 16px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{s.student.name}</h1>
          <div className="muted">
            {s.student.rollNumber ? `${s.student.rollNumber} · ` : ""}
            {s.assignment.title} · submitted {new Date(s.submittedAt).toLocaleString()}
            {s.late && <span className="badge warn" style={{ marginLeft: 6 }}>late</span>}
          </div>
        </div>
        {isMcq ? (
          <span className={`badge ${(s.autoScore ?? 0) > 0 ? "pass" : "fail"}`}>
            {(s.autoScore ?? 0) > 0 ? "correct" : "incorrect"}
          </span>
        ) : (
          <span className={`badge ${passed === s.testResults.length && s.testResults.length > 0 ? "pass" : "fail"}`}>
            {passed} / {s.testResults.length} tests
          </span>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      {isStaff && (
        <div className="card">
          <div className="between" style={{ marginBottom: 12 }}>
            <strong>Grade</strong>
            <span className="muted">Auto score: {s.autoScore ?? "—"} / {s.assignment.maxMarks}</span>
          </div>
          <div className="grid-2">
            <label>
              <span>Final marks (out of {s.assignment.maxMarks}) — blank keeps the auto score</span>
              <input
                type="number"
                min={0}
                max={s.assignment.maxMarks}
                step="0.5"
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
              />
            </label>
            <label>
              <span>Comment to the student</span>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
            </label>
          </div>
          <div className="row">
            <button className="primary" onClick={() => saveGrade(false)} disabled={saving}>
              {saving ? "Saving…" : "Save grade"}
            </button>
            {nextId && (
              <button onClick={() => saveGrade(true)} disabled={saving}>
                Save and next unreviewed
              </button>
            )}
            <span className="muted" style={{ fontSize: 13 }}>
              {remaining > 0
                ? `${remaining} more waiting on a teacher`
                : "This is the last one waiting."}
            </span>
          </div>
        </div>
      )}

      {isStaff && !isMcq && (
        <AiReviewPanel
          submissionId={id!}
          maxMarks={s.assignment.maxMarks}
          autoScore={s.autoScore ?? 0}
          onAccepted={(total) => {
            setMarks(String(total));
            setStatus(`Accepted. Final mark is now ${total} of ${s.assignment.maxMarks}.`);
          }}
        />
      )}

      {s.compilerOutput && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>COMPILER OUTPUT</div>
          <pre className="console">{s.compilerOutput}</pre>
        </div>
      )}

      {!isMcq && (
      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>TEST RESULTS</div>
        {s.testResults.length === 0 && <div className="muted">No tests were run (the code did not compile).</div>}
        {s.testResults.map((r) => (
          <div key={r.id} className="test-row" style={{ display: "block" }}>
            <div className="row" style={{ marginBottom: r.passed ? 0 : 8 }}>
              <span className={`badge ${r.passed ? "pass" : "fail"}`}>{r.passed ? "pass" : "fail"}</span>
              <span className="name" style={{ flex: 1 }}>
                {r.testCase.name}{" "}
                {r.testCase.hidden && <span className="muted" style={{ fontSize: 12 }}>(hidden)</span>}
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                {r.timeMs} ms
                {r.timedOut && " · timed out"}
                {r.signal && ` · ${r.signal}`}
                {r.exitCode !== null && ` · exit ${r.exitCode}`}
              </span>
            </div>
            {!r.passed && (
              <>
                <pre className="console">{r.actualStdout || "(no output)"}</pre>
                {r.stderr && <pre className="console" style={{ marginTop: 6, color: "var(--fail-text)" }}>{r.stderr}</pre>}
              </>
            )}
          </div>
        ))}
      </div>
      )}

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>
          {isMcq ? "ANSWER" : "SUBMITTED CODE"}
        </div>
        {isMcq ? (
          <McqAnswer
            options={s.options ?? []}
            selected={s.selectedOptionIds ?? []}
            onChange={() => {}}
            multiple
            disabled
            review
          />
        ) : (
          <div style={{ height: 420, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
            <Editor
              language="c"
              theme={editorTheme}
              value={s.code}
              options={{ readOnly: true, fontSize: 13, minimap: { enabled: false }, automaticLayout: true }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * AI-assisted partial credit for one submission.
 *
 * Proposes marks to ADD to the test-based score for work that shows the right
 * approach, and explains why against the question's criteria. Nothing is
 * applied until a teacher accepts — and the teacher can accept a different
 * number, which is what makes this a review rather than a rubber stamp.
 *
 * Absent entirely unless the question has been opted in (a rubric plus a
 * non-zero cap), so a teacher who has not set one up never sees a control that
 * would only tell them it is unavailable.
 */
function AiReviewPanel({
  submissionId,
  maxMarks,
  autoScore,
  onAccepted,
}: {
  submissionId: string;
  maxMarks: number;
  autoScore: number;
  onAccepted: (total: number) => void;
}) {
  const [result, setResult] = useState<AiReviewResponse | null>(null);
  const [credit, setCredit] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decided, setDecided] = useState<"ACCEPTED" | "REJECTED" | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<AiReviewResponse>(`/api/submissions/${submissionId}/ai-review`, {});
      setResult(r);
      setCredit(String(r.review.aiSuggestedScore ?? 0));
      setDecided(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The review could not be completed.");
    } finally {
      setBusy(false);
    }
  }

  async function decide(accept: boolean) {
    setBusy(true);
    setError(null);
    try {
      const body = accept ? { accept: true, credit: Number(credit) || 0 } : { accept: false };
      const r = await api.post<{ manualScore: number | null; aiStatus: string }>(
        `/api/submissions/${submissionId}/ai-review/decision`,
        body
      );
      setDecided(accept ? "ACCEPTED" : "REJECTED");
      if (accept && r.manualScore !== null) onAccepted(r.manualScore);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record the decision.");
    } finally {
      setBusy(false);
    }
  }

  const awards: AiAward[] = result?.review.aiCriteria
    ? (JSON.parse(result.review.aiCriteria) as AiAward[])
    : [];
  const byId = new Map((result?.criteria ?? []).map((c) => [c.id, c]));
  const proposedTotal = Math.round((autoScore + (Number(credit) || 0)) * 100) / 100;

  return (
    <div className="card">
      <div className="between" style={{ marginBottom: 10 }}>
        <strong>Partial credit for approach</strong>
        <button onClick={run} disabled={busy}>
          {busy ? "Reviewing…" : result ? "Review again" : "Review with AI"}
        </button>
      </div>

      {!result && !error && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Judges whether this submission shows the right approach even though its tests failed, and
          proposes marks to add on top of the auto score. Nothing is applied until you accept it.
        </p>
      )}

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div>
          {result.review.aiFlagged && (
            <div className="notice" style={{ marginBottom: 10 }}>
              The model was unsure about this one — worth reading closely.
            </div>
          )}

          <p style={{ marginTop: 0 }}>{result.review.aiRationale}</p>

          {awards.length > 0 && (
            <table style={{ marginBottom: 10 }}>
              <thead>
                <tr><th>Criterion</th><th>Met</th><th>Awarded</th><th>Note</th></tr>
              </thead>
              <tbody>
                {awards.map((a) => (
                  <tr key={a.criterionId}>
                    <td>{byId.get(a.criterionId)?.text ?? "(removed criterion)"}</td>
                    <td>
                      <span className={`badge ${a.met ? "pass" : "fail"}`}>{a.met ? "yes" : "no"}</span>
                    </td>
                    <td>{a.awarded} / {byId.get(a.criterionId)?.marks ?? "?"}</td>
                    <td className="muted">{a.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {decided ? (
            <div className="notice">
              {decided === "ACCEPTED"
                ? "Accepted — the mark above has been updated."
                : "Rejected. The suggestion is kept on the record but awards nothing."}
            </div>
          ) : (
            <>
              <label className="row" style={{ width: "auto", alignItems: "center" }}>
                <span style={{ marginBottom: 0 }}>Marks to add</span>
                <input
                  type="number"
                  min={0}
                  max={result.maxCredit}
                  step="0.5"
                  style={{ width: 90 }}
                  value={credit}
                  onChange={(e) => setCredit(e.target.value)}
                />
                <span className="muted" style={{ marginBottom: 0 }}>
                  of at most {result.maxCredit} · {autoScore} + {Number(credit) || 0} ={" "}
                  <strong>{proposedTotal}</strong> of {maxMarks}
                </span>
              </label>
              <div className="row" style={{ marginTop: 10 }}>
                <button className="primary" onClick={() => decide(true)} disabled={busy}>
                  Accept
                </button>
                <button onClick={() => decide(false)} disabled={busy}>Reject</button>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {result.review.aiModel}, rubric v{result.review.aiRubricVersion ?? "?"} — recorded
                  with the mark so it can be reconstructed later.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
